import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { LoadingLogo } from '@/components/shared/LoadingLogo';
import { PageHeader } from '@/components/shared/PageHeader';
import { TableCard } from '@/components/shared/TableCard';
import { TablePagination } from '@/components/shared/TablePagination';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useIndustryTranslation } from '@/hooks/useIndustryTranslation';
import { usePaginationFromUrl } from '@/hooks/usePaginationFromUrl';
import { listDetectionsPaginated } from '@/services/detectionService';
import { listItems } from '@/services/itemService';
import { listBranches } from '@/services/branchService';
import { listScanners } from '@/services/scannerService';
import { listShelves } from '@/services/shelfService';
import type { Detection, Item, Scanner, Branch, Shelf } from '@packages/shared-types';

export function DetectionsPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const title = useIndustryTranslation('pages.detections.title');
  const description = useIndustryTranslation('pages.detections.description');
  const loadingLabel = useIndustryTranslation('pages.detections.list.loading');
  const emptyLabel = useIndustryTranslation('pages.detections.list.empty');
  const errorLoad = useIndustryTranslation('pages.detections.error.load');

  const pagination = usePaginationFromUrl();

  const [detections, setDetections] = useState<Detection[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [scanners, setScanners] = useState<Scanner[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const [detectionsResult, itemsData, scannersData, branchesData, shelvesData] =
        await Promise.all([
          listDetectionsPaginated({
            page: pagination.page,
            limit: pagination.limit,
          }),
          listItems(),
          listScanners(),
          listBranches(),
          listShelves(),
        ]);
      setDetections(detectionsResult.data);
      pagination.setTotal(detectionsResult.total);
      setItems(itemsData);
      setScanners(scannersData);
      setBranches(branchesData);
      setShelves(shelvesData);
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : errorLoad;
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [pagination.page, pagination.limit, errorLoad]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleRowClick = useCallback(
    (id: string) => () => {
      navigate(`/detections/${id}`);
    },
    [navigate],
  );

  const getScanTypeLabel = (scanType: string): string =>
    t(`pages.items.details.scanTypes.${scanType}`, { defaultValue: scanType });

  return (
    <div className="space-y-6 p-6">
      <PageHeader title={title} description={description} />

      {error && (
        <Card className="border-destructive bg-destructive/10">
          <CardContent className="p-4">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      <TableCard>
        {isLoading ? (
          <div className="flex flex-col items-center gap-3 py-8">
            <LoadingLogo size="sm" />
            <span className="text-sm text-muted-foreground">{loadingLabel}</span>
          </div>
        ) : detections.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {emptyLabel}
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('pages.detections.table.detectedAt')}</TableHead>
                  <TableHead>{t('pages.detections.table.itemName')}</TableHead>
                  <TableHead>{t('pages.detections.table.sku')}</TableHead>
                  <TableHead>{t('pages.detections.table.scanner')}</TableHead>
                  <TableHead>{t('pages.detections.table.branch')}</TableHead>
                  <TableHead>{t('pages.detections.table.shelf')}</TableHead>
                  <TableHead>{t('pages.detections.table.scanType')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detections.map((detection) => {
                  const item = items.find((i) => i.id === detection.itemId);
                  const scanner = scanners.find((s) => s.id === detection.scannerId);
                  const branch = branches.find((b) => b.id === detection.branchId);
                  const shelf = detection.shelfId
                    ? shelves.find((s) => s.id === detection.shelfId)
                    : null;
                  return (
                    <TableRow
                      key={detection.id}
                      onRowClick={handleRowClick(detection.id)}
                    >
                      <TableCell className="text-muted-foreground">
                        {new Date(detection.detectedAt).toLocaleString()}
                      </TableCell>
                      <TableCell className="font-medium">
                        {item?.name ?? '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {item?.sku ?? '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {scanner?.name ?? '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {branch?.name ?? '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {shelf?.name ?? '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {getScanTypeLabel(detection.scanType)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <TablePagination
              pagination={pagination}
              rowsPerPageLabel={t('pages.common.pagination.rowsPerPage')}
              pageOfLabel={t('pages.common.pagination.pageOf', {
                current: pagination.page,
                total: pagination.totalPages,
              })}
              onRefresh={() => void loadData()}
              isRefreshing={isLoading}
            />
          </>
        )}
      </TableCard>
    </div>
  );
}
